import React, { useState, useEffect } from "react";
import axios from "axios";
import API_BASE from "./config";
import "./css/ModifyWorkflow.css";
import { usePermissions, RequirePermission } from "./hooks/usePermissions";


const ModifyWorkflow = ({ chatId, workflowId, workflowMetadataId, cognitoUserId, organization }) => {
  const [workflowData, setWorkflowData] = useState(null);
  const [svgContent, setSvgContent] = useState("");
  const [svgUrl, setSvgUrl] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [question, setQuestion] = useState("");
  const [response, setResponse] = useState("");
  const [conversation, setConversation] = useState([]);
  const [logLines, setLogLines] = useState([]);
  const [disableInput, setDisableInput] = useState(false);

  useEffect(() => {
    const fetchWorkflowDetails = async () => {
      try {
        const res = await axios.get(`${API_BASE}/get-workflow-details/${workflowMetadataId}`);
        const details = res.data.workflowDetails[0];
        setWorkflowData(details);

        if (details?.workflow_svg_path) {
          const svgRes = await fetch(`${API_BASE}/${details.workflow_svg_path}`);
          if (svgRes.ok) {
            const svgText = await svgRes.text();
            setSvgContent(svgText);
          }

          const svgFolder = details.workflow_svg_path.split("/").slice(0, -1).join("/");
          const mainSvgPath = `${svgFolder}/main.svg`;
          const mainSvgRes = await fetch(`${API_BASE}/${mainSvgPath}`);
          if (mainSvgRes.ok) {
            setSvgUrl(`${API_BASE}/${mainSvgPath}`);
          }
        }

        setLoading(false);
      } catch (err) {
        console.error("Error loading workflow details:", err);
        setError("Failed to load workflow details.");
        setLoading(false);
      }
    };

    fetchWorkflowDetails();
  }, [workflowId, workflowMetadataId]);

  const Spinner = () => (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <span
        style={{
          display: "inline-block",
          width: "16px",
          height: "16px",
          marginRight: "8px",
          border: "2px solid #ccc",
          borderTop: "2px solid #007BFF",
          borderRadius: "50%",
          animation: "spin 1s linear infinite"
        }}
      />
    </>
  );

  const askModification = async () => {
    const updatedConversation = [...conversation, { role: "user", content: question }];
    setConversation(updatedConversation);
    setResponse("Thinking...");

    // 1. Fetch MySQL user ID using Cognito user ID
    const res = await axios.get(
      `${API_BASE}/get-user-id-by-cognito-user-id/${cognitoUserId}`
    );

    const mysqlUserId = res.data.mysql_user_id;
    console.log("MySQL User ID:", mysqlUserId);

    // 2. Build payload with MySQL user ID included
    try {
      const payload = {
        workflow_id: workflowId,
        cognito_user_id: cognitoUserId,
        organization,
        messages: updatedConversation,
        mysql_user_id: mysqlUserId
      };

      const res = await fetch(`${API_BASE}/claude-api-modify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const rawText = await res.text();
      
      const firstDataIndex = rawText.indexOf("data:");
      if (firstDataIndex === -1) {
        setResponse("No response received.");
        setQuestion("");
        return;
      }

      // 2) Find status block (if any)
      const statusMarker = "\ndata: STATUS:";
      const statusIndex = rawText.indexOf(statusMarker);

      let htmlBlock;
      if (statusIndex !== -1) {
        // Take everything between first "data:" and before "data: STATUS:"
        htmlBlock = rawText.substring(firstDataIndex + "data:".length, statusIndex);
      } else {
        // No status marker; take everything after first "data:"
        htmlBlock = rawText.substring(firstDataIndex + "data:".length);
      }

      htmlBlock = htmlBlock.trim();

      // 3) Extract status (if present)
      let statusText = "";
      if (statusIndex !== -1) {
        const afterStatus = rawText.substring(statusIndex + "data:".length).trim(); // "STATUS: PENDING\n\ndata: DONE..."
        const statusLineEnd = afterStatus.indexOf("\n");
        const statusLine = statusLineEnd !== -1 ? afterStatus.substring(0, statusLineEnd) : afterStatus;
        // statusLine should be like "STATUS: PENDING"
        const statusParts = statusLine.split(":");
        if (statusParts.length > 1) {
          statusText = statusParts[1].trim();
        }
      }

      // 4) Update UI
      if (htmlBlock) {
        setResponse(htmlBlock);
        setConversation(prev => [
          ...prev,
          { role: "assistant", content: htmlBlock }
        ]);
      } else if (statusText) {
        setResponse(`Status: ${statusText}`);
      } else {
        setResponse("No structured response received.");
      }

      // If status ever becomes COMPLETE, we can disable input and redirect
      if (statusText.toUpperCase && statusText.toUpperCase() === "COMPLETE") {
        setDisableInput(true);
        setTimeout(() => {
          window.location.href = "/all-chats";
        }, 400);
      }

      setQuestion("");
    } catch (err) {
      console.error("Error calling modify API:", err);
      setResponse("An error occurred while processing your request.");
      setQuestion("");
    }
  };

  if (loading) return <p>Loading workflow details...</p>;
  if (error) return <p>{error}</p>;

  let parsedContainers = {};
  try {
    const cleaned = workflowData.containers_list
      .replace(/^{,/, "{")
      .replace(/,,/g, ",")
      .replace(/,\s*}/, "}");
    parsedContainers = JSON.parse(cleaned);
  } catch (err) {
    console.warn("Invalid containers_list JSON:", err);
  }

  return (
    <RequirePermission permission="modifyBuilds" showMessage>
    <div style={{ padding: "20px" }}>
      <h3>
        Modify <em className="workflow_name">{workflowData?.short_name || "this workflow"}</em> Workflow
      </h3>

      <h4>Workflow Summary</h4>
      {workflowData.workflow_summary ? (
        <p>{workflowData.workflow_summary}</p>
      ) : (
        <p>No workflow summary available.</p>
      )}
      
      <RequirePermission permission="modifyBuilds">
      <h4>Let me know how you want me to modify this build</h4>
      <textarea
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        placeholder="Describe the changes you want to make..."
        rows={5}
        style={{
          width: "100%",
          padding: "10px",
          borderRadius: "8px",
          border: "1px solid #ccc",
          marginBottom: "10px",
        }}
        disabled={disableInput}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            askModification();
          }
        }}
      />
      <button
        onClick={askModification}
        disabled={!question.trim() || disableInput}
        style={{
          padding: "10px 20px",
          backgroundColor: disableInput ? "#999" : "#007BFF",
          color: "#fff",
          border: "none",
          borderRadius: "5px",
          cursor: disableInput ? "not-allowed" : "pointer",
          marginBottom: "20px"
        }}
      >
      Modify
      </button>

      <div
        style={{
          padding: "15px",
          border: "1px solid #dcdcdc",
          borderRadius: "8px",
          backgroundColor: "#fafafa",
          minHeight: "120px"
        }}
      >
        <strong>Response:</strong>
        <div style={{ marginTop: "8px" }}>
          {response === "Thinking..." ? (
            <div style={{ display: "flex", alignItems: "center" }}>
              <Spinner />
              <span>Thinking...</span>
            </div>
          ) : (
            <>
              {/* render full HTML block exactly as received */}
              <div
                style={{ whiteSpace: "normal", overflow: "visible" }}
                dangerouslySetInnerHTML={{ __html: response || "Awaiting your input..." }}
              />
            </>
          )}
        </div>
      </div>
      </RequirePermission>

      <h4>Workflow Diagram</h4>
      {svgContent || svgUrl ? (
        <div style={{ display: "flex", gap: "20px", alignItems: "flex-start", flexWrap: "wrap" }}>
          {svgContent && (
            <pre
              style={{
                backgroundColor: "#f9f9f9",
                padding: "10px",
                borderRadius: "5px",
                overflowX: "auto",
                whiteSpace: "pre-wrap",
                maxWidth: "600px"
              }}
            >
              {svgContent}
            </pre>
          )}
          {svgUrl && (
            <div style={{ maxWidth: "600px" }}>
              <img
                src={svgUrl}
                alt="Workflow DAG"
                style={{ width: "100%", border: "1px solid #ccc", borderRadius: "5px" }}
              />
            </div>
          )}
        </div>
      ) : (
        <p>Workflow diagram not available.</p>
      )}

      <h4>Programs Used in Analysis</h4>
      {Object.keys(parsedContainers).length > 0 ? (
        <table style={{ borderCollapse: "collapse", width: "100%", marginBottom: "20px" }}>
          <thead>
            <tr>
              <th
                style={{
                  border: "1px solid #ccc",
                  padding: "8px",
                  backgroundColor: "#f2f2f2",
                  width: "20%"
                }}
              >
                STEP
              </th>
              <th
                style={{
                  border: "1px solid #ccc",
                  padding: "8px",
                  backgroundColor: "#f2f2f2"
                }}
              >
                PROGRAMS USED
              </th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(parsedContainers).map(([step, programs], index) => (
              <tr key={index}>
                <td
                  style={{
                    border: "1px solid #ccc",
                    padding: "8px",
                    fontWeight: "bold"
                  }}
                >
                  {step}
                </td>
                <td
                  style={{
                    border: "1px solid #ccc",
                    padding: "8px"
                  }}
                >
                  {programs.join(", ")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p>No containers listed for this workflow.</p>
      )}

      
    </div>
    </RequirePermission>
  );
};

export default ModifyWorkflow;